import React, { useEffect, useRef } from 'react';
import { TimeUnit } from 'chart.js';
import { Empty } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import ApexCharts from 'apexcharts';
import { getToolTipLabelForDuration } from "@src/components/Common/chartUtils";
import LoadingVeil from "@src/ui/LoadingVeil";

interface FlowbiteUnifiedBarChartProps {
    avgTimeData: any;
    changesData: any;
    unit: TimeUnit;
    loading?: boolean;
    /** set when the request failed, as opposed to returning nothing */
    error?: string | null;
}

const FlowbiteUnifiedBarChart: React.FC<FlowbiteUnifiedBarChartProps> = ({
    avgTimeData,
    changesData,
    unit,
    loading,
    error
}) => {
    const chartRef = useRef<HTMLDivElement>(null);
    /* The box the chart is measured against, which is deliberately NOT the box
       it renders into. Apex writes an explicit pixel height onto its host, so
       measuring that host feeds its own output back in: each resize read a
       height Apex had just set and set it again, and the chart ratcheted to
       several thousand pixels inside a 600px card. */
    const wrapRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<ApexCharts | null>(null);
    /** the element the chart was built into, to tell an update from a rebuild */
    const chartHost = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!chartRef.current || !avgTimeData?.datasets || !changesData?.datasets) {
            return;
        }

        // Prepare data for ApexCharts
        const avgTimeDataset = avgTimeData.datasets[0];
        const changesDataset = changesData.datasets[0];

        // Convert labels to appropriate format
        const rawLabels = avgTimeData.labels || [];
        const rawAvgTimeValues = avgTimeDataset?.data || [];
        const rawChangesValues = changesDataset?.data || [];

        // Helper function to get week key for aggregation
        const getAggregationKey = (dateStr: string) => {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const year = parseInt(parts[2], 10);
                const date = new Date(year, month, day);

                if (unit === 'week') {
                    // Get Monday of the week (week start)
                    const monday = new Date(date);
                    const dayOfWeek = date.getDay();
                    const daysToMonday = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek); // Handle Sunday = 0
                    monday.setDate(date.getDate() + daysToMonday);
                    // Return in DD/MM/YYYY format to match expected format
                    const day = String(monday.getDate()).padStart(2, '0');
                    const month = String(monday.getMonth() + 1).padStart(2, '0');
                    const year = monday.getFullYear();
                    return `${day}/${month}/${year}`;
                } else if (unit === 'month') {
                    // Group by month-year (DD/MM/YYYY format)
                    return `01/${String(month + 1).padStart(2, '0')}/${year}`;
                }
            }
            // For daily or fallback, use original date
            return dateStr;
        };

        // Aggregate data by appropriate time unit
        const dataMap = new Map<string, { avgTime: number[], changes: number[], count: number, originalDate: Date }>();

        rawLabels.forEach((label: string, index: number) => {
            const avgValue = rawAvgTimeValues[index] || 0;
            const changeValue = rawChangesValues[index] || 0; // Handle undefined values
            const aggregationKey = getAggregationKey(label);

            // Parse original date for later use
            const parts = label.split('/');
            const originalDate = parts.length === 3
                ? new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10))
                : new Date(label);

            if (dataMap.has(aggregationKey)) {
                const existing = dataMap.get(aggregationKey)!;
                existing.avgTime.push(avgValue);
                existing.changes.push(changeValue);
                existing.count++;
                // Keep the earliest date for the period
                if (originalDate < existing.originalDate) {
                    existing.originalDate = originalDate;
                }
            } else {
                dataMap.set(aggregationKey, {
                    avgTime: [avgValue],
                    changes: [changeValue],
                    count: 1,
                    originalDate
                });
            }
        });

        // Create deduplicated arrays by averaging duplicate values
        const labels: string[] = [];
        const avgTimeValues: number[] = [];
        const changesValues: number[] = [];

        // Helper function to parse date strings consistently
        const parseDateForSorting = (dateStr: string) => {
            // Handle DD/MM/YYYY format from backend
            const parts = dateStr.split('/');
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
                const year = parseInt(parts[2], 10);
                return new Date(year, month, day);
            }
            // Fallback to direct parsing
            return new Date(dateStr);
        };

        // Sort entries by date to maintain chronological order
        const sortedEntries = Array.from(dataMap.entries()).sort((a, b) => {
            const dateA = parseDateForSorting(a[0]);
            const dateB = parseDateForSorting(b[0]);
            return dateA.getTime() - dateB.getTime();
        });

        sortedEntries.forEach(([label, data]) => {
            labels.push(label);
            // For average time, take the mean of all values for that date
            const avgTime = data.avgTime.reduce((sum, val) => sum + val, 0) / data.avgTime.length;
            avgTimeValues.push(avgTime);
            // For changes, sum all values for that date
            const totalChanges = data.changes.reduce((sum, val) => sum + val, 0);
            changesValues.push(totalChanges);
        });

        // Parse and format dates for x-axis
        // The dates come in DD/MM/YYYY format from backend
        const parseDateString = (dateStr: string) => {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
                // Backend sends DD/MM/YYYY, so parts[0] = day, parts[1] = month, parts[2] = year
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
                const year = parseInt(parts[2], 10);

                return new Date(year, month, day);
            }
            // Fallback to direct parsing
            return new Date(dateStr);
        };

        // Prepare data series with proper date parsing
        const parsedDates = labels.map(parseDateString);

        // For bar charts, we'll use simple values and let categories handle the x-axis
        const avgTimeSeries = avgTimeValues;
        const changesSeries = changesValues;

        // Format dates for display (full dates for tooltips)
        const formattedCategories = parsedDates.map(date => {
            switch (unit) {
                case 'month':
                    return date.toLocaleDateString('en-UK', { month: 'short', year: 'numeric' });
                case 'week':
                    return date.toLocaleDateString('en-UK', { month: 'short', day: 'numeric', year: '2-digit' });
                case 'day':
                    return date.toLocaleDateString('en-UK', { month: 'short', day: 'numeric', year: '2-digit' });
                default:
                    return date.toLocaleDateString('en-UK', { month: 'short', day: 'numeric', year: '2-digit' });
            }
        });

        // Create separate categories for x-axis labels
        const xAxisCategories = (() => {
            if (unit === 'day' && formattedCategories.length > 2) {
                // For daily: show only first and last dates
                return formattedCategories.map((category, index) => {
                    if (index === 0 || index === formattedCategories.length - 1) {
                        return category;
                    }
                    return '';
                });
            } else if (unit === 'week') {
                // For weekly: show all week start dates (since we're now properly aggregating by week)
                return formattedCategories;
            }
            // For monthly or other units: show all dates
            return formattedCategories;
        })();


        const options = {
            chart: {
                /* The card decides how tall the chart is, not the chart. This
                   was a fixed 400px, so on a big screen the chart sat in the
                   top half of its card with the rest of it empty. */
                height: wrapRef.current?.clientHeight || 400,
                type: 'line' as const,
                fontFamily: 'Inter, sans-serif',
                dropShadow: {
                    enabled: false,
                },
                toolbar: {
                    show: true,
                },
                offsetX: 0,
                offsetY: 0,
                zoom: {
                    enabled: false,
                },
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '60%',
                    endingShape: 'rounded',
                    borderRadius: 4,
                    dataLabels: {
                        position: 'top',
                    },
                },
            },
            stroke: {
                width: [3, 0], // Line width for series 1 (avg time), no stroke for series 2 (bars)
                curve: 'smooth',
                dashArray: [0, 0] // Solid line for avg time, no dash for bars
            },
            tooltip: {
                enabled: true,
                marker: {
                    show: false,
                },
                x: {
                    show: true,
                    format: unit === 'month' ? 'MMM yyyy' :
                           unit === 'week' ? 'MMM dd, yyyy' :
                           unit === 'day' ? 'MMM dd, yyyy' :
                           'MMM dd, yyyy',
                    formatter: function(value: any, opts: any) {
                        // Use original formatted categories for tooltips to show all dates
                        const index = opts?.dataPointIndex;
                        if (index !== undefined && formattedCategories[index]) {
                            return formattedCategories[index];
                        }
                        return value;
                    },
                },
                y: [
                    {
                        formatter: function(value: number) {
                            return getToolTipLabelForDuration(avgTimeData.yLabel, value);
                        },
                        title: {
                            formatter: function() {
                                return 'Average Time: ';
                            }
                        }
                    },
                    {
                        formatter: function(value: number) {
                            return `${Math.round(value)} changes`;
                        },
                        title: {
                            formatter: function() {
                                return 'Completed: ';
                            }
                        }
                    }
                ]
            },
            dataLabels: {
                enabled: false,
            },
            series: [
                {
                    name: 'Average Time to Complete',
                    type: 'line',
                    data: avgTimeSeries,
                    color: '#103075ff',
                },
                {
                    name: 'Number of Completed Changes',
                    type: 'column',
                    data: changesSeries,
                    color: '#559fb8ff',
                },
            ],
            colors: ['#103075ff', '#559fb8ff'],
            markers: {
                size: [5, 0],
                colors: ['#103075ff', '#559fb8ff'],
                strokeColors: '#fff',
                strokeWidth: 2,
                hover: {
                    sizeOffset: 2,
                },
            },
            legend: {
                show: true,
                position: 'top' as const,
                horizontalAlign: 'left' as const,
            },
            xaxis: {
                type: 'category' as const,
                categories: xAxisCategories,
                labels: {
                    show: true,
                    style: {
                        fontFamily: 'Inter, sans-serif',
                        cssClass: 'text-xs font-normal fill-gray-500 dark:fill-gray-400'
                    },
                    rotate: (unit === 'day' || unit === 'week') ? -90 : 0,
                    rotateAlways: false,
                    hideOverlappingLabels: true,
                    maxHeight: 120,
                    offsetY: unit === 'day' ? 5 : 0,
                    trim: true,
                    minHeight: unit === 'day' ? 60 : undefined,
                },
                axisBorder: {
                    show: false,
                },
                axisTicks: {
                    show: false,
                },
                tooltip: {
                    enabled: false,
                },
                tickPlacement: 'on' as const,
            },
            yaxis: [
                {
                    // Primary Y-axis for Average Time
                    seriesName: 'Average Time to Complete',
                    title: {
                        text: avgTimeData.yLabel || 'Average Time',
                        style: {
                            color: '#103075ff',
                            fontSize: '12px',
                            fontWeight: 600,
                        }
                    },
                    labels: {
                        style: {
                            colors: ['#103075ff'],
                        },
                        formatter: function(value: number) {
                            return getToolTipLabelForDuration(avgTimeData.yLabel, value);
                        },
                        offsetX: -10,
                    },
                    min: 0,
                    tickAmount: 6,
                    forceNiceScale: true,
                },
                {
                    // Secondary Y-axis for Number of Changes
                    opposite: true,
                    seriesName: 'Number of Completed Changes',
                    title: {
                        text: 'Number of Changes',
                        style: {
                            color: '#559fb8ff',
                            fontSize: '12px',
                            fontWeight: 600,
                        }
                    },
                    labels: {
                        style: {
                            colors: ['#559fb8ff'],
                        },
                        formatter: function(value: number) {
                            return Math.round(value).toString();
                        },
                        offsetX: 10,
                    },
                },
            ],
            grid: {
                show: true,
                borderColor: '#e1e8ed',
                strokeDashArray: 1,
                position: 'back' as const,
                xaxis: {
                    lines: {
                        show: false,
                    }
                },
                yaxis: {
                    lines: {
                        show: true,
                    }
                },
                padding: {
                    left: 20,
                    right: 20,
                    top: 10,
                    bottom: 10
                },
            }
        };

        /* A new answer updates the chart that is there rather than destroying
           it and drawing another. Rebuilt, it went blank for a frame and drew
           itself in from zero on every filter change — the chart blinked.
           Updated, its bars and line move from the old values to the new.
           It is only built again when its host is a different element: the
           canvas left for the empty or failed state and has come back. */
        if (chartInstance.current && chartHost.current === chartRef.current) {
            chartInstance.current.updateOptions(options);
        } else {
            chartInstance.current?.destroy();
            chartInstance.current = new ApexCharts(chartRef.current, options);
            chartHost.current = chartRef.current;
            chartInstance.current.render();
        }

        // Add resize observer for smoother resize handling
        let resizeObserver: ResizeObserver | null = null;
        let resizeTimeout: NodeJS.Timeout;

        if (wrapRef.current) {
            resizeObserver = new ResizeObserver((entries) => {
                // Debounce resize calls to prevent too many updates
                clearTimeout(resizeTimeout);
                const box = entries[0]?.contentRect;
                resizeTimeout = setTimeout(() => {
                    if (chartInstance.current && box) {
                        // Both dimensions, taken from the wrapper: it used to
                        // follow the card's width and ignore its height, so
                        // growing the window sideways resized the chart and
                        // growing it downwards just added blank space.
                        chartInstance.current.updateOptions({
                            chart: {
                                width: box.width,
                                height: box.height,
                            }
                        }, false, false); // No animation for better sync
                    }
                }, 10); // Very short delay for smooth resizing
            });

            resizeObserver.observe(wrapRef.current);
        }

        // the chart itself outlives this run; see the unmount effect below
        return () => {
            clearTimeout(resizeTimeout);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [avgTimeData, changesData, unit]);

    useEffect(() => () => {
        chartInstance.current?.destroy();
        chartInstance.current = null;
    }, []);

    /* Emptiness is a property of the series, not of the shape around it. The
       hook always sets `datasets` on a successful response — even one with no
       intervals in it — so testing for the key drew a chart with labelled axes
       and nothing plotted whenever a team had no completed changes yet. The
       key does say whether there has been an answer at all. */
    const answered = !!avgTimeData?.datasets;
    const hasPoints = [avgTimeData, changesData].some(
        (set: any) => (set?.datasets ?? []).some((d: any) => (d?.data ?? []).length > 0),
    );

    /* Loading is a veil over whatever was last drawn, rather than a spinner in
       its place: the chart that was there, the empty state, or — before the
       first answer — the bare canvas. Swapping in a spinner rebuilt the chart
       from nothing on every filter change, and drew it a different height. */
    let content: React.ReactNode;
    if (error) {
        content = (
            <div className="dash-chart__state dash-chart__state--bad" role="alert">
                <ExclamationCircleFilled aria-hidden="true" />
                <span>{error}</span>
            </div>
        );
    } else if (answered && !hasPoints) {
        content = (
            <div className="dash-chart__state">
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No completed changes in this period"
                />
            </div>
        );
    } else {
        content = (
            <div className="dash-chart__canvas" ref={wrapRef}>
                <div ref={chartRef} id="flowbite-bar-chart" />
            </div>
        );
    }

    return (
        <LoadingVeil loading={!!loading} label="Loading the chart" fill>
            {content}
        </LoadingVeil>
    );
};

export default FlowbiteUnifiedBarChart;